import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { createEvent } from '../../services/firestoreService';

const CreateGrandReLaunch = () => {
  const { currentUser, hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const isAdmin = hasRole('admin');

  const grandReLaunchData = {
    title: "APN's Grand ReLaunch Event!",
    description: "Join APN for our Grand ReOpening event for an evening of networking!",
    longDescription: `
      <p>APN's Grand ReLaunch Event! Join us for an exciting evening of networking, connections, and opportunities as we officially relaunch the Accelerated Professionals Network.</p>
      
      <p>This special event marks a new chapter for APN, bringing together finance professionals from across the industry. Whether you're in investment banking, private equity, wealth management, or any other finance specialty, this is your chance to connect with like-minded professionals and be part of our growing community.</p>
      
      <p>The event will include:</p>
      <ul>
        <li>Welcome reception with cocktails and appetizers</li>
        <li>Networking opportunities with industry professionals</li>
        <li>Presentation on APN's vision and upcoming initiatives</li>
        <li>Interactive discussions and Q&A sessions</li>
        <li>Business card exchange and connection facilitation</li>
      </ul>
      
      <p>Don't miss this opportunity to be part of APN's exciting new beginning!</p>
    `,
    date: "Wednesday, October 8, 2025 at 6:00 PM",
    time: "6:00 PM - 9:00 PM",
    startDate: new Date("2025-10-08T18:00:00-04:00"), // 6 PM EST
    endDate: new Date("2025-10-08T21:00:00-04:00"), // 9 PM EST
    location: "Jack Doyle's",
    address: "240 W 35th, New York, NY 10001",
    image: "/images/events/grand-relaunch-event.jpg",
    featured: true,
    eventType: "in-person",
    maxAttendees: 150,
    
    // New pricing structure (sponsors use discount codes)
    pricing: {
      member: 125.00,
      nonMember: 160.00
      // Note: Sponsors use discount codes instead of fixed pricing
    },
    
    // Legacy ticketTypes for compatibility
    ticketTypes: [
      {
        id: 'member',
        name: 'Member Registration',
        price: 125.00,
        description: 'For current APN members',
        memberOnly: true
      },
      {
        id: 'non-member',
        name: 'Non-Member Registration',
        price: 160.00,
        description: 'For non-members',
        memberOnly: false
      },
      // Note: Sponsor tickets removed - sponsors use discount codes instead
    ],
    
    sponsors: [
      {
        id: 1,
        name: 'Goldman Sachs',
        level: 'Gold',
        attendeeLimit: 10,
        additionalAttendeeDiscount: 50
      },
      {
        id: 2,
        name: 'JP Morgan',
        level: 'Silver',
        attendeeLimit: 6,
        additionalAttendeeDiscount: 25
      },
      {
        id: 3,
        name: 'Bank of America',
        level: 'Bronze',
        attendeeLimit: 4,
        additionalAttendeeDiscount: 15
      }
    ],
    
    attendees: [],
    registrations: [],
    
    accessDates: {
      basic: '2025-10-01T00:00:00Z', // One week before event
      bronze: '2025-09-24T00:00:00Z', // Two weeks before event
      silver: '2025-09-17T00:00:00Z', // 3 weeks before event
      gold: '2025-09-10T00:00:00Z'    // 4 weeks before event
    },
    
    registrationDeadline: new Date("2025-10-07T23:59:59-04:00"), // Day before event
    
    comments: [],
    reactions: {
      like: 15,
      celebrate: 8,
      support: 5,
      insightful: 3,
      curious: 12
    },
    
    // SEO and metadata
    slug: "grand-relaunch-event",
    tags: ["networking", "relaunch", "finance", "professionals", "nyc"],
    
    // Event status
    status: "published",
    isVirtual: false,
    requiresApproval: false
  };

  const handleCreateEvent = async () => {
    if (!isAdmin) {
      setMessage('❌ Admin access required');
      return;
    }

    setLoading(true);
    setMessage('🚀 Creating Grand ReLaunch Event...');

    try {
      const eventId = await createEvent(grandReLaunchData);
      
      setMessage(`✅ Grand ReLaunch Event created successfully!
      
Event ID: ${eventId}
Title: ${grandReLaunchData.title}
Date: ${grandReLaunchData.date}
Location: ${grandReLaunchData.location}
Pricing: Member $125, Non-Member $160 (Sponsors use discount codes)

The event is now live and should display proper price ranges and time!`);
      
    } catch (error) {
      console.error('Error creating event:', error);
      setMessage(`❌ Error creating event: ${error.message || error.code || 'Unknown error'}

If this is a permission error, the Firebase security rules may be blocking the operation.`);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">Please log in to access admin tools.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-800 mb-2">Admin Access Required</h2>
          <p className="text-red-600">You need admin privileges to create events.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Create Grand ReLaunch Event</h1>
        
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Event Details Preview:</h2>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <p><strong>Title:</strong> {grandReLaunchData.title}</p>
            <p><strong>Date:</strong> {grandReLaunchData.date}</p>
            <p><strong>Time:</strong> {grandReLaunchData.time}</p>
            <p><strong>Location:</strong> {grandReLaunchData.location}</p>
            <p><strong>Address:</strong> {grandReLaunchData.address}</p>
            <p><strong>Pricing:</strong> Member $125, Non-Member $160 (Sponsors use discount codes)</p>
            <p><strong>Max Attendees:</strong> {grandReLaunchData.maxAttendees}</p>
          </div>
        </div>

        <button
          onClick={handleCreateEvent}
          disabled={loading}
          className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition duration-200 ${
            loading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-apn-blue hover:bg-blue-700'
          }`}
        >
          {loading ? 'Creating Event...' : 'Create Grand ReLaunch Event'}
        </button>

        {message && (
          <div className={`mt-6 p-4 rounded-lg ${
            message.includes('✅') 
              ? 'bg-green-50 border border-green-200 text-green-800' 
              : message.includes('❌') 
              ? 'bg-red-50 border border-red-200 text-red-800'
              : 'bg-blue-50 border border-blue-200 text-blue-800'
          }`}>
            <pre className="whitespace-pre-wrap font-mono text-sm">{message}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateGrandReLaunch;
